/**
 * PageObserver - Passive page content extraction for silent mode.
 *
 * Extracts DOM content (headings, paragraphs, links, forms, pricing cards,
 * buttons, etc.) from each page visitors browse and reports it to the backend.
 * Used in silent mode to build site context before the widget goes live.
 *
 * Uses MutationObserver to detect DOM stability before extracting, which
 * handles SPA content that loads asynchronously.
 *
 * Tracks reported URLs in localStorage to avoid re-sending already-mapped pages.
 * Supports SPA navigation via pushState/replaceState/popstate hooks.
 */

import type { ApiClient } from './api-client';
import type { WidgetConfig } from './config';
import { isAuthenticatedFromStorage } from './config';
import { createLogger } from './logger';

const log = createLogger('PageObserver');

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PricingCard {
  planName: string;
  price: string | null;
  period: string | null;
  description: string | null;
  features: string[];
  ctaText: string | null;
  badge: string | null;
  highlighted: boolean;
}

export interface LocationCard {
  name: string;
  address: string | null;
  cityPostal: string | null;
  phone: string | null;
}

export interface PageContext {
  url: string;
  title: string;
  metaDescription: string | null;
  headings: { level: number; text: string }[];
  paragraphs: string[];
  links: { text: string; href: string }[];
  images: { src: string; alt: string }[];
  forms: { action: string; fields: string[] }[];
  tables: { headers: string[]; rowCount: number }[];
  lists: { items: string[] }[];
  navigation: { text: string; href: string }[];
  structuredData: Record<string, unknown>[];
  pricingCards: PricingCard[];
  locationCards: LocationCard[];
  buttons: string[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Initial delay before starting MutationObserver (let framework bootstrap) */
const INITIAL_DELAY_MS = 500;

/** How long DOM must be quiet before we consider it stable */
const DOM_SETTLE_MS = 1500;

/** Max time to wait for DOM to stabilize */
const DOM_SETTLE_TIMEOUT_MS = 8000;

/**
 * Extractor version — bump this when extraction logic improves.
 * When the widget detects a version change, it clears the localStorage
 * cache so all pages get re-extracted with the improved extractors.
 */
const EXTRACTOR_VERSION = 4;

/** localStorage key for tracking reported URLs */
const STORAGE_KEY_PREFIX = 'pms_mapped_';

/** localStorage key for extractor version */
const VERSION_KEY_PREFIX = 'pms_extractor_v_';

/** Max paragraphs/text blocks to extract per page */
const MAX_PARAGRAPHS = 100;

/** Max links to extract per page */
const MAX_LINKS = 200;

/** Max images to extract per page */
const MAX_IMAGES = 50;

/** Max buttons to extract per page */
const MAX_BUTTONS = 50;

/** Min text length to include (skip tiny fragments) */
const MIN_TEXT_LENGTH = 15;

/** Cooldown between re-extractions for the same URL (ms) */
const RE_EXTRACT_COOLDOWN_MS = 10000;

/** Min new content items to trigger a re-send (avoid noise from tiny changes) */
const RE_EXTRACT_MIN_DIFF = 3;

// ---------------------------------------------------------------------------
// PageObserver Class
// ---------------------------------------------------------------------------

export class PageObserver {
  private reportedUrls: Set<string>;
  private apiClient: ApiClient;
  private config: WidgetConfig;
  private workspaceId: string;
  private storageKey: string;

  /** Last extracted content per URL — used to detect meaningful changes on re-extract */
  private lastExtraction = new Map<string, PageContext>();

  /** Timestamps of last send per URL — for cooldown enforcement */
  private lastSendTime = new Map<string, number>();

  /** Whether the interaction listener is active */
  private interactionListenerActive = false;

  constructor(apiClient: ApiClient, config: WidgetConfig) {
    this.apiClient = apiClient;
    this.config = config;
    this.workspaceId = config.workspaceId;
    this.storageKey = STORAGE_KEY_PREFIX + config.workspaceId;

    // Check extractor version — if changed, clear cache so pages re-extract
    this.checkExtractorVersion();
    this.reportedUrls = this.loadReportedUrls();
  }

  /**
   * Start observing. Waits for DOM stability before extracting,
   * then hooks into SPA navigation for subsequent pages.
   * Also listens for user interactions that may reveal new content.
   */
  start(): void {
    // Small initial delay to let framework bootstrap, then use MutationObserver
    setTimeout(() => this.waitForDomStable().then(() => this.observeCurrentPage()), INITIAL_DELAY_MS);

    // Hook SPA navigation
    this.hookNavigation();

    // Listen for user interactions (tab clicks, toggles, sliders)
    this.startInteractionListener();

    log.debug(`Started — ${this.reportedUrls.size} pages already mapped`);
  }

  // =========================================================================
  // DOM Stability Detection
  // =========================================================================

  /**
   * Wait for the DOM to stabilize using MutationObserver.
   * Resolves when no mutations occur for DOM_SETTLE_MS,
   * or after DOM_SETTLE_TIMEOUT_MS (whichever comes first).
   */
  private waitForDomStable(): Promise<void> {
    return new Promise((resolve) => {
      let settleTimer: ReturnType<typeof setTimeout>;
      let timeoutTimer: ReturnType<typeof setTimeout>;

      const observer = new MutationObserver(() => {
        // Reset settle timer on each mutation
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => {
          observer.disconnect();
          clearTimeout(timeoutTimer);
          resolve();
        }, DOM_SETTLE_MS);
      });

      // Start observing
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false,
      });

      // Start initial settle timer (in case DOM is already stable)
      settleTimer = setTimeout(() => {
        observer.disconnect();
        clearTimeout(timeoutTimer);
        resolve();
      }, DOM_SETTLE_MS);

      // Hard timeout — don't wait forever
      timeoutTimer = setTimeout(() => {
        observer.disconnect();
        clearTimeout(settleTimer);
        log.debug('DOM settle timeout — extracting anyway');
        resolve();
      }, DOM_SETTLE_TIMEOUT_MS);
    });
  }

  // =========================================================================
  // Core Observation
  // =========================================================================

  private async observeCurrentPage(): Promise<void> {
    const url = this.normalizeUrl(window.location.href);
    if (!url) return;

    // Skip if already reported (initial extraction only — re-extraction uses different path)
    if (this.reportedUrls.has(url)) {
      log.debug(`Skip (already mapped): ${url}`);
      return;
    }

    try {
      const context = this.extractPageContent();
      await this.reportToBackend(context);

      this.reportedUrls.add(url);
      this.saveReportedUrls();
      this.lastExtraction.set(url, context);
      this.lastSendTime.set(url, Date.now());

      log.debug(`Mapped: ${url} (${context.paragraphs.length} text blocks, ${context.pricingCards.length} pricing cards, ${context.buttons.length} buttons)`);
    } catch (error) {
      log.warn(`Failed to observe ${url}:`, error);
    }
  }

  // =========================================================================
  // DOM Extraction
  // =========================================================================

  private extractPageContent(): PageContext {
    const ctx: PageContext = {
      url: window.location.href,
      title: document.title || '',
      metaDescription: this.getMeta('description'),
      headings: this.extractHeadings(),
      paragraphs: this.extractTextContent(),
      links: this.extractLinks(),
      images: this.extractImages(),
      forms: this.extractForms(),
      tables: this.extractTables(),
      lists: this.extractLists(),
      navigation: this.extractNavigation(),
      structuredData: this.extractJsonLd(),
      pricingCards: this.extractPricingCards(),
      locationCards: this.extractLocationCards(),
      buttons: this.extractButtons(),
    };

    // Authenticated pages contain user-specific data (admin views, profiles, etc.).
    // Strip down to structural/UI-only content so we capture page layout,
    // not actual user data that would pollute the knowledge base.
    if (this.isVisitorAuthenticated()) {
      return this.stripUserData(ctx);
    }

    return ctx;
  }

  /**
   * Check if the current visitor is authenticated.
   * Uses explicit config flag or auto-detects from localStorage tokens.
   */
  private isVisitorAuthenticated(): boolean {
    return this.config.isAuthenticated
      ?? isAuthenticatedFromStorage(this.config.authTokenKeys);
  }

  /**
   * Strip user-specific content from page context.
   * Keeps structural elements (headings, nav, form fields, buttons, table headers)
   * that describe the UI layout. Drops content that is likely rendered from
   * user/product/admin data (paragraphs, lists, images, links, structured data).
   */
  private stripUserData(ctx: PageContext): PageContext {
    return {
      url: ctx.url,
      title: ctx.title,
      metaDescription: ctx.metaDescription,
      headings: ctx.headings,
      paragraphs: [],
      links: [],
      images: [],
      forms: ctx.forms,
      tables: ctx.tables.map(t => ({ headers: t.headers, rowCount: 0 })),
      lists: [],
      navigation: ctx.navigation,
      structuredData: [],
      pricingCards: ctx.pricingCards,
      locationCards: [],
      buttons: ctx.buttons,
    };
  }

  private getMeta(name: string): string | null {
    const el =
      document.querySelector(`meta[name="${name}"]`) ||
      document.querySelector(`meta[property="og:${name}"]`);
    return el?.getAttribute('content') || null;
  }

  private extractHeadings(): PageContext['headings'] {
    const headings: PageContext['headings'] = [];
    document.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((el) => {
      const text = (el.textContent || '').trim();
      if (text) {
        const level = parseInt(el.tagName.charAt(1), 10);
        headings.push({ level, text });
      }
    });
    return headings;
  }

  /**
   * Extract meaningful text content from the page.
   *
   * Goes beyond just <p> tags — also captures text from:
   * - <span>, <div>, <section>, <article> with direct text content
   * - Price-containing elements (currency symbols, patterns like $99/mo)
   * - Description text in cards, callouts, etc.
   *
   * Does NOT filter by visibility — captures content behind tabs/toggles/sliders
   * since that content is in the DOM but may be display:none.
   */
  private extractTextContent(): string[] {
    const texts: string[] = [];
    const seen = new Set<string>();

    // 1. Standard paragraph elements (highest priority)
    document.querySelectorAll('p').forEach((el) => {
      if (texts.length >= MAX_PARAGRAPHS) return;
      if (this.isWidgetElement(el)) return;
      const text = (el.textContent || '').trim();
      if (text.length >= MIN_TEXT_LENGTH && !seen.has(text)) {
        seen.add(text);
        texts.push(text);
      }
    });

    // 2. Block-level elements that contain direct meaningful text
    //    (not just inherited from child p/h/li tags)
    const blockSelectors = 'div, span, section, article, aside, blockquote, figcaption, [class*="description"], [class*="subtitle"], [class*="tagline"], [class*="summary"]';
    document.querySelectorAll(blockSelectors).forEach((el) => {
      if (texts.length >= MAX_PARAGRAPHS) return;
      if (this.isWidgetElement(el)) return;
      const directText = this.getDirectTextContent(el);
      if (directText.length >= MIN_TEXT_LENGTH && !seen.has(directText)) {
        seen.add(directText);
        texts.push(directText);
      }
    });

    return texts;
  }

  /**
   * Get text content that is directly owned by an element,
   * excluding text from child block-level elements to avoid duplication.
   */
  private getDirectTextContent(el: Element): string {
    const blockTags = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'TD', 'TH', 'BLOCKQUOTE', 'PRE', 'UL', 'OL', 'TABLE', 'FORM', 'NAV']);
    let text = '';
    for (const node of el.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent || '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const child = node as Element;
        // Include text from inline elements (span, strong, em, a, etc.)
        if (!blockTags.has(child.tagName)) {
          text += child.textContent || '';
        }
      }
    }
    return text.trim();
  }

  private extractLinks(): PageContext['links'] {
    const links: PageContext['links'] = [];
    const seen = new Set<string>();
    const origin = window.location.origin;

    document.querySelectorAll('a[href]').forEach((el) => {
      if (links.length >= MAX_LINKS) return;
      const href = el.getAttribute('href') || '';
      // Skip anchors, javascript, mailto, tel
      if (!href || href.startsWith('#') || href.startsWith('javascript:')
          || href.startsWith('mailto:') || href.startsWith('tel:')) return;

      // Resolve to absolute URL
      let absolute = href;
      if (href.startsWith('/')) {
        absolute = origin + href;
      }

      // Deduplicate
      const normalized = this.normalizeUrl(absolute);
      if (!normalized || seen.has(normalized)) return;
      seen.add(normalized);

      const text = (el.textContent || '').trim();
      links.push({ text: text || href, href: absolute });
    });
    return links;
  }

  private extractImages(): PageContext['images'] {
    const images: PageContext['images'] = [];
    document.querySelectorAll('img[src]').forEach((el) => {
      if (images.length >= MAX_IMAGES) return;
      const src = el.getAttribute('src') || '';
      const alt = el.getAttribute('alt') || '';
      // Skip tracking pixels and tiny images
      const img = el as HTMLImageElement;
      if (img.naturalWidth > 0 && img.naturalWidth < 3) return;
      if (src && !src.startsWith('data:')) {
        images.push({ src, alt });
      }
    });
    return images;
  }

  private extractForms(): PageContext['forms'] {
    const forms: PageContext['forms'] = [];
    document.querySelectorAll('form').forEach((el) => {
      const action = el.getAttribute('action') || '';
      const fields: string[] = [];
      el.querySelectorAll('input, select, textarea').forEach((input) => {
        const name = input.getAttribute('name') || input.getAttribute('id') || '';
        const type = input.getAttribute('type') || 'text';
        if (name && type !== 'hidden' && type !== 'submit') {
          fields.push(name);
        }
      });
      if (fields.length > 0) {
        forms.push({ action, fields });
      }
    });
    return forms;
  }

  private extractTables(): PageContext['tables'] {
    const tables: PageContext['tables'] = [];
    document.querySelectorAll('table').forEach((el) => {
      const headers: string[] = [];
      el.querySelectorAll('th').forEach((th) => {
        const text = (th.textContent || '').trim();
        if (text) headers.push(text);
      });
      const rowCount = el.querySelectorAll('tbody tr, tr').length;
      if (headers.length > 0 || rowCount > 0) {
        tables.push({ headers, rowCount });
      }
    });
    return tables;
  }

  private extractLists(): PageContext['lists'] {
    const lists: PageContext['lists'] = [];
    document.querySelectorAll('ul, ol').forEach((el) => {
      // Skip navigation lists (handled separately)
      if (el.closest('nav')) return;
      const items: string[] = [];
      el.querySelectorAll(':scope > li').forEach((li) => {
        const text = (li.textContent || '').trim();
        if (text && text.length < 500) {
          items.push(text);
        }
      });
      if (items.length >= 2) {
        lists.push({ items });
      }
    });
    return lists;
  }

  private extractNavigation(): PageContext['navigation'] {
    const nav: PageContext['navigation'] = [];
    const seen = new Set<string>();

    document.querySelectorAll('nav a[href], [role="navigation"] a[href]').forEach((el) => {
      const href = el.getAttribute('href') || '';
      if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;

      let absolute = href;
      if (href.startsWith('/')) {
        absolute = window.location.origin + href;
      }

      if (seen.has(absolute)) return;
      seen.add(absolute);

      const text = (el.textContent || '').trim();
      if (text) {
        nav.push({ text, href: absolute });
      }
    });
    return nav;
  }

  private extractJsonLd(): Record<string, unknown>[] {
    const results: Record<string, unknown>[] = [];
    document.querySelectorAll('script[type="application/ld+json"]').forEach((el) => {
      try {
        const data = JSON.parse(el.textContent || '');
        if (data && typeof data === 'object') {
          results.push(data as Record<string, unknown>);
        }
      } catch {
        // Invalid JSON-LD, skip
      }
    });
    return results;
  }

  // =========================================================================
  // Pricing Card Extraction
  // =========================================================================

  /** Price regex used across multiple pricing detection strategies */
  private static readonly PRICE_PATTERN = /[$£€¥₹]\s*\d+|(?:\d+[.,]\d{2})\s*[/$£€]|\bfree\b/i;

  /**
   * Detect and extract pricing card structures.
   *
   * Uses three strategies in order:
   * 1. Class/attribute-based selectors (explicit pricing CSS classes)
   * 2. Heading-proximity detection (h2-h4 near price patterns — works with
   *    any framework including Angular/React custom elements)
   * 3. Sibling detection (groups of siblings that contain price patterns)
   *
   * Extracts ALL cards including those behind toggles/tabs (hidden in DOM).
   */
  private extractPricingCards(): PricingCard[] {
    const cards: PricingCard[] = [];

    // Strategy 1: Find pricing containers by class/attribute patterns
    const pricingContainerSelectors = [
      '[class*="pricing"] [class*="card"]',
      '[class*="pricing"] [class*="plan"]',
      '[class*="pricing"] [class*="tier"]',
      '[class*="pricing"] [class*="column"]',
      '[class*="plan-card"]',
      '[class*="price-card"]',
      '[class*="pricing-card"]',
      '[class*="pricing-plan"]',
      '[class*="pricing-tier"]',
      '[class*="package-card"]',
      '[data-plan]',
      '[data-pricing]',
    ];

    const candidateCards = new Set<Element>();
    for (const selector of pricingContainerSelectors) {
      try {
        document.querySelectorAll(selector).forEach(el => {
          if (!this.isWidgetElement(el)) candidateCards.add(el);
        });
      } catch { /* invalid selector, skip */ }
    }

    // Strategy 2: Heading-proximity detection
    // Find h2-h4 headings whose container has a price pattern nearby.
    // Works regardless of class names or custom element wrappers (Angular, React, etc.)
    if (candidateCards.size === 0) {
      this.findPriceCardsByHeadingProximity(candidateCards);
    }

    // Strategy 3: Sibling detection (any element type, not just div)
    if (candidateCards.size === 0) {
      this.findPriceCardsBySiblings(candidateCards);
    }

    // Extract structured data from each candidate card
    for (const cardEl of candidateCards) {
      const card = this.parsePricingCard(cardEl);
      if (card && (card.price || card.planName)) {
        cards.push(card);
      }
    }

    // Deduplicate by plan name + price
    const seen = new Set<string>();
    return cards.filter(c => {
      const key = `${c.planName}|${c.price}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /**
   * Strategy 2: Find pricing cards by heading proximity.
   *
   * For each h2-h4, walk up the DOM to find the smallest ancestor that
   * contains a price pattern but is still card-sized (< 1500 chars).
   * If 2+ such containers are found, they're pricing cards.
   *
   * This handles Angular/React custom elements, Shadow DOM wrappers,
   * and any class naming convention.
   */
  private findPriceCardsByHeadingProximity(candidates: Set<Element>): void {
    const cardContainers: Element[] = [];

    document.querySelectorAll('h2, h3, h4').forEach(heading => {
      if (this.isWidgetElement(heading)) return;

      // Walk up to find the smallest container with both this heading and a price
      let el: Element | null = heading.parentElement;
      for (let depth = 0; depth < 6 && el; depth++) {
        const text = el.textContent || '';
        // Container must have a price AND be card-sized (not the whole page)
        if (PageObserver.PRICE_PATTERN.test(text) && text.length < 1500) {
          cardContainers.push(el);
          break;
        }
        el = el.parentElement;
      }
    });

    // Need 2+ cards to be considered a pricing section
    if (cardContainers.length >= 2) {
      // Filter out containers that are ancestors of other containers
      // (keep only the most specific ones)
      const filtered = cardContainers.filter(container =>
        !cardContainers.some(other => other !== container && container.contains(other))
      );
      filtered.forEach(c => candidates.add(c));
    }
  }

  /**
   * Strategy 3: Find pricing cards by looking for sibling elements with price patterns.
   * Queries all element types (not just div) to handle custom elements.
   */
  private findPriceCardsBySiblings(candidates: Set<Element>): void {
    // Check children of likely container elements for sibling groups with prices
    const containerCandidates = document.querySelectorAll(
      'div, section, article, main, [class*="pricing"], [class*="plans"], [class*="tiers"]'
    );

    for (const parent of containerCandidates) {
      if (this.isWidgetElement(parent)) continue;

      // Check direct children of ANY element type (handles Angular/React components)
      const childrenWithPrices: Element[] = [];
      for (const child of parent.children) {
        const text = child.textContent || '';
        // Child must contain a price and be card-sized
        if (PageObserver.PRICE_PATTERN.test(text) && text.length < 1500 && text.length > 10) {
          childrenWithPrices.push(child);
        }
      }

      // If this parent has 2+ children with prices, those are likely pricing cards
      if (childrenWithPrices.length >= 2) {
        childrenWithPrices.forEach(c => candidates.add(c));
        return; // Found cards, stop searching
      }
    }
  }

  /**
   * Parse a single pricing card element into structured data.
   */
  private parsePricingCard(el: Element): PricingCard | null {
    const text = el.textContent || '';
    if (!text.trim()) return null;

    // Extract plan name from headings or prominent text
    const planName = this.extractPlanName(el);

    // Extract price (look for currency patterns)
    const price = this.extractPrice(el);

    // Extract period (monthly, yearly, /mo, /yr, etc.)
    const period = this.extractPricePeriod(el);

    // Extract description
    const desc = el.querySelector('[class*="description"], [class*="subtitle"], [class*="tagline"], p');
    const description = desc ? (desc.textContent || '').trim() : null;

    // Extract features from lists
    const features: string[] = [];
    el.querySelectorAll('li, [class*="feature"]').forEach(li => {
      const featureText = (li.textContent || '').trim();
      if (featureText && featureText.length < 200 && featureText.length > 2) {
        features.push(featureText);
      }
    });

    // Extract CTA button text
    const cta = el.querySelector('button, a[class*="btn"], a[class*="cta"], [class*="cta"], a[role="button"]');
    const ctaText = cta ? (cta.textContent || '').trim() : null;

    // Extract badge (popular, best value, recommended, etc.)
    const badgeEl = el.querySelector('[class*="badge"], [class*="popular"], [class*="recommended"], [class*="best"], [class*="ribbon"]');
    const badge = badgeEl ? (badgeEl.textContent || '').trim() : null;

    // Check if this card is highlighted/featured
    const classes = el.className?.toLowerCase() || '';
    const highlighted = /highlight|featured|popular|recommended|active|selected/.test(classes);

    return {
      planName: planName || '',
      price: price || null,
      period: period || null,
      description: description && description.length < 300 ? description : null,
      features: features.slice(0, 20),
      ctaText,
      badge,
      highlighted,
    };
  }

  /**
   * Extract the plan/tier name from a pricing card element.
   */
  private extractPlanName(el: Element): string {
    // Try headings first
    const heading = el.querySelector('h1, h2, h3, h4, h5, h6');
    if (heading) {
      const text = (heading.textContent || '').trim();
      if (text.length > 0 && text.length < 60) return text;
    }

    // Try elements with plan/tier class names
    const nameEl = el.querySelector('[class*="name"], [class*="title"], [class*="plan-name"], [class*="tier-name"]');
    if (nameEl) {
      const text = (nameEl.textContent || '').trim();
      if (text.length > 0 && text.length < 60) return text;
    }

    return '';
  }

  /**
   * Extract a price value from an element.
   * Looks for currency symbols and numeric patterns.
   */
  private extractPrice(el: Element): string | null {
    // Try elements specifically marked as price
    const priceSelectors = '[class*="price"], [class*="amount"], [class*="cost"], [data-price]';
    const priceEls = el.querySelectorAll(priceSelectors);
    for (const priceEl of priceEls) {
      const text = (priceEl.textContent || '').trim();
      const match = text.match(/(?:[$£€¥₹]\s*[\d,]+(?:\.\d{1,2})?|[\d,]+(?:\.\d{1,2})?\s*[$£€¥₹]|\bfree\b)/i);
      if (match) return match[0];
    }

    // Fallback: scan all text nodes for price patterns
    const allText = el.textContent || '';
    const match = allText.match(/(?:[$£€¥₹]\s*[\d,]+(?:\.\d{1,2})?|[\d,]+(?:\.\d{1,2})?\s*[$£€¥₹]|\bfree\b)/i);
    return match ? match[0] : null;
  }

  /**
   * Extract billing period from a pricing element.
   */
  private extractPricePeriod(el: Element): string | null {
    const text = el.textContent || '';
    const patterns = [
      /\/\s*(mo(?:nth)?|yr|year|week|day|annually|monthly|yearly)/i,
      /per\s+(month|year|week|day|user|seat)/i,
      /billed?\s+(monthly|yearly|annually|quarterly)/i,
    ];
    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match) return match[0];
    }
    return null;
  }

  // =========================================================================
  // Button / CTA Extraction
  // =========================================================================

  /**
   * Extract meaningful button and CTA text from the page.
   * Detect and extract location/address card structures.
   *
   * Uses heading-proximity detection (parallel to pricing card extraction):
   * finds groups of sibling containers that each have a heading (h2-h4)
   * and address-like text (street + postal/city + optional phone).
   *
   * Works regardless of CSS class naming conventions — catches proprietary
   * CMS layouts that don't use standard address/location classes.
   */
  private extractLocationCards(): LocationCard[] {
    const cards: LocationCard[] = [];

    // EU postal+city: "8610 Uster", "4153 Reinach BL"
    // US: "Denver, CO 80202"
    const postalCityRe = /^\d{4,5}\s+[A-Za-zÀ-ÿ][\w\s./-]+$|^[A-Za-zÀ-ÿ][\w\s./-]+,?\s+[A-Z]{2}\s+\d{5}$/;
    // Street: "Bahnstrasse 1", "123 Main Street", "Don Bosco-Strasse 1"
    const streetRe = /^[A-Za-zÀ-ÿ][\w\s./-]+\s+\d+\w?$|^\d+\s+[A-Za-zÀ-ÿ][\w\s./-]+$/;
    // Phone: digit sequences with optional +, spaces, dashes, parens
    const phoneRe = /^\+?[0-9][0-9\s()/-]{6,}[0-9]$/;

    // Group headings by their card-level ancestor
    const parentToCards = new Map<Element, Element[]>();
    document.querySelectorAll('h2, h3, h4').forEach((heading) => {
      if (this.isWidgetElement(heading)) return;
      let card: Element | null = heading.parentElement;
      if (!card) return;

      // Walk up to find the card boundary (deepest ancestor whose parent has 2+ children)
      for (let i = 0; i < 3 && card; i++) {
        const grandparent = card.parentElement;
        if (grandparent && grandparent.children.length >= 2) break;
        card = card.parentElement;
      }
      if (card?.parentElement) {
        const parent = card.parentElement;
        const existing = parentToCards.get(parent) || [];
        existing.push(card);
        parentToCards.set(parent, existing);
      }
    });

    // Need 2+ sibling cards to be a location grid
    for (const [, siblings] of parentToCards) {
      if (siblings.length < 2) continue;

      const candidates: LocationCard[] = [];
      for (const card of siblings) {
        const h = card.querySelector('h2, h3, h4');
        if (!h) continue;
        const name = (h.textContent || '').trim();
        if (!name) continue;

        const ps = card.querySelectorAll('p');
        let address: string | null = null;
        let cityPostal: string | null = null;
        let phone: string | null = null;

        ps.forEach((p) => {
          const t = (p.textContent || '').trim();
          if (!t) return;
          if (streetRe.test(t) && !address) address = t;
          else if (postalCityRe.test(t) && !cityPostal) cityPostal = t;
          else if (phoneRe.test(t) && !phone) phone = t;
        });

        if (address || cityPostal) {
          candidates.push({ name, address, cityPostal, phone });
        }
      }

      if (candidates.length >= 2) {
        cards.push(...candidates);
      }
    }

    return cards;
  }

  /**
   * Captures buttons, CTA links, and action elements.
   * Includes hidden buttons (behind toggles/tabs).
   */
  private extractButtons(): string[] {
    const buttons: string[] = [];
    const seen = new Set<string>();

    const selectors = 'button, a[class*="btn"], a[class*="cta"], [role="button"], input[type="submit"], input[type="button"], a[class*="button"]';

    document.querySelectorAll(selectors).forEach(el => {
      if (buttons.length >= MAX_BUTTONS) return;
      if (this.isWidgetElement(el)) return;

      let text = '';
      if (el.tagName === 'INPUT') {
        text = (el as HTMLInputElement).value || '';
      } else {
        text = (el.textContent || '').trim();
      }

      // Filter out meaningless buttons
      if (!text || text.length < 2 || text.length > 80) return;
      // Skip common non-meaningful buttons
      if (/^(×|x|close|menu|toggle|hamburger|\.\.\.)$/i.test(text)) return;

      if (!seen.has(text.toLowerCase())) {
        seen.add(text.toLowerCase());
        buttons.push(text);
      }
    });

    return buttons;
  }

  // =========================================================================
  // Interaction Re-extraction
  // =========================================================================

  /**
   * Listen for user interactions that may reveal new content.
   *
   * When a user clicks a tab, toggle, slider, or accordion, new content
   * often appears in the DOM. We watch for clicks that cause DOM mutations,
   * then re-extract and send an update if the content changed meaningfully.
   */
  private startInteractionListener(): void {
    if (this.interactionListenerActive) return;
    this.interactionListenerActive = true;

    document.addEventListener('click', (event) => {
      const target = event.target as Element;
      if (!target || this.isWidgetElement(target)) return;

      // Only watch for clicks on interactive-looking elements
      // (tabs, toggles, accordions, buttons within the page — not nav links)
      const interactive = target.closest(
        'button, [role="tab"], [role="button"], [class*="tab"], [class*="toggle"], ' +
        '[class*="switch"], [class*="accordion"], [class*="slider"], [class*="filter"], ' +
        '[data-toggle], [data-tab], [data-slide]'
      );
      if (!interactive) return;

      // Skip if it's a navigation link (full page change handled by hookNavigation)
      if (interactive.tagName === 'A' && interactive.getAttribute('href')) return;

      // Wait for DOM to settle after the interaction, then re-extract
      this.scheduleReExtraction();
    }, { capture: true, passive: true });
  }

  /** Pending re-extraction timer (debounce rapid clicks) */
  private reExtractTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Schedule a re-extraction after a user interaction.
   * Debounces rapid clicks and enforces cooldown per URL.
   */
  private scheduleReExtraction(): void {
    if (this.reExtractTimer) clearTimeout(this.reExtractTimer);

    this.reExtractTimer = setTimeout(() => {
      this.reExtractTimer = null;
      this.waitForDomStable().then(() => this.reExtractCurrentPage());
    }, INITIAL_DELAY_MS);
  }

  /**
   * Re-extract the current page and send an update if content changed meaningfully.
   */
  private async reExtractCurrentPage(): Promise<void> {
    const url = this.normalizeUrl(window.location.href);
    if (!url) return;

    // Only re-extract pages we've already mapped
    if (!this.reportedUrls.has(url)) return;

    // Enforce cooldown
    const lastSend = this.lastSendTime.get(url) || 0;
    if (Date.now() - lastSend < RE_EXTRACT_COOLDOWN_MS) {
      log.debug(`Re-extract cooldown: ${url}`);
      return;
    }

    try {
      const newContext = this.extractPageContent();
      const oldContext = this.lastExtraction.get(url);

      if (!this.hasSignificantChanges(oldContext, newContext)) {
        log.debug(`Re-extract: no significant changes for ${url}`);
        return;
      }

      await this.reportToBackend(newContext);
      this.lastExtraction.set(url, newContext);
      this.lastSendTime.set(url, Date.now());

      log.debug(`Re-extracted: ${url} (content updated after interaction)`);
    } catch (error) {
      log.warn(`Re-extract failed for ${url}:`, error);
    }
  }

  /**
   * Check if the new extraction has meaningfully more content than the old one.
   * Returns true if there are enough new items to justify a re-send.
   */
  private hasSignificantChanges(
    oldCtx: PageContext | undefined,
    newCtx: PageContext
  ): boolean {
    if (!oldCtx) return true;

    let newItems = 0;

    // Count new paragraphs/text blocks
    const oldTexts = new Set(oldCtx.paragraphs);
    newItems += newCtx.paragraphs.filter(p => !oldTexts.has(p)).length;

    // Count new pricing cards
    const oldCardKeys = new Set(oldCtx.pricingCards.map(c => `${c.planName}|${c.price}`));
    newItems += newCtx.pricingCards.filter(c => !oldCardKeys.has(`${c.planName}|${c.price}`)).length;

    // Count new buttons
    const oldButtons = new Set(oldCtx.buttons.map(b => b.toLowerCase()));
    newItems += newCtx.buttons.filter(b => !oldButtons.has(b.toLowerCase())).length;

    // Count new headings
    const oldHeadings = new Set(oldCtx.headings.map(h => h.text));
    newItems += newCtx.headings.filter(h => !oldHeadings.has(h.text)).length;

    // Count new list items
    const oldListItems = new Set(oldCtx.lists.flatMap(l => l.items));
    const newListItems = newCtx.lists.flatMap(l => l.items);
    newItems += newListItems.filter(i => !oldListItems.has(i)).length;

    return newItems >= RE_EXTRACT_MIN_DIFF;
  }

  // =========================================================================
  // Widget Element Check
  // =========================================================================

  /**
   * Check if an element belongs to the PromptMySite widget itself.
   * Prevents the observer from extracting its own UI content.
   */
  private isWidgetElement(el: Element): boolean {
    return !!el.closest('#promptmysite-widget, [data-pms-widget]');
  }

  // =========================================================================
  // SPA Navigation Hooks
  // =========================================================================

  private hookNavigation(): void {
    // Hook pushState and replaceState for SPA routing
    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);

    history.pushState = (...args: Parameters<typeof history.pushState>) => {
      originalPushState(...args);
      this.onNavigate();
    };

    history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
      originalReplaceState(...args);
      this.onNavigate();
    };

    // Also listen for popstate (back/forward)
    window.addEventListener('popstate', () => this.onNavigate());
  }

  private onNavigate(): void {
    // Wait for DOM to stabilize after SPA navigation, then extract
    setTimeout(() => this.waitForDomStable().then(() => this.observeCurrentPage()), INITIAL_DELAY_MS);
  }

  // =========================================================================
  // Backend Reporting
  // =========================================================================

  private async reportToBackend(context: PageContext): Promise<void> {
    const apiUrl = this.apiClient.getBaseUrl();

    const response = await fetch(
      `${apiUrl}/api/workspaces/${this.workspaceId}/discovery/page-context`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(context),
      }
    );

    if (!response.ok) {
      throw new Error(`Page context report failed: ${response.status}`);
    }
  }

  // =========================================================================
  // URL Helpers
  // =========================================================================

  private normalizeUrl(url: string): string | null {
    try {
      const u = new URL(url);
      // Strip fragment and common tracking params
      u.hash = '';
      u.searchParams.delete('utm_source');
      u.searchParams.delete('utm_medium');
      u.searchParams.delete('utm_campaign');
      u.searchParams.delete('utm_content');
      u.searchParams.delete('utm_term');
      // Strip trailing slash (except root)
      let path = u.pathname;
      if (path.length > 1 && path.endsWith('/')) {
        path = path.slice(0, -1);
      }
      return u.origin + path + u.search;
    } catch {
      return null;
    }
  }

  // =========================================================================
  // localStorage Persistence
  // =========================================================================

  private loadReportedUrls(): Set<string> {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const urls: string[] = JSON.parse(stored);
        return new Set(urls);
      }
    } catch {
      // Corrupted data, start fresh
    }
    return new Set();
  }

  private saveReportedUrls(): void {
    try {
      const urls = Array.from(this.reportedUrls);
      // Cap at 500 URLs to avoid localStorage bloat
      const capped = urls.slice(-500);
      localStorage.setItem(this.storageKey, JSON.stringify(capped));
    } catch {
      // localStorage full or unavailable, ignore
    }
  }

  // =========================================================================
  // Extractor Versioning
  // =========================================================================

  /**
   * Check extractor version against what's stored in localStorage.
   * If the version changed (new extraction code deployed), clear the
   * URL cache so all pages get re-extracted with the improved extractors.
   */
  private checkExtractorVersion(): void {
    const versionKey = VERSION_KEY_PREFIX + this.workspaceId;
    try {
      const storedVersion = localStorage.getItem(versionKey);
      const currentVersion = String(EXTRACTOR_VERSION);

      if (storedVersion !== currentVersion) {
        // Version changed — clear URL cache to force re-extraction
        log.debug(`Extractor version changed (${storedVersion} -> ${currentVersion}), clearing mapped URL cache`);
        localStorage.removeItem(this.storageKey);
        localStorage.setItem(versionKey, currentVersion);
      }
    } catch {
      // localStorage unavailable, skip version check
    }
  }
}
